import { useState } from 'react';
import { webhooks, ApiClientError } from '../lib/api';
import { useResource } from '../hooks/useResource';
import { formatRelative } from '../lib/format';
import { Alert, Badge, Card, CopyButton, Empty, Modal, Spinner } from '../components/ui';

export function Webhooks() {
  const endpoints = useResource(() => webhooks.list(), []);

  const [creating, setCreating] = useState(false);
  const [url, setUrl] = useState('');
  const [mode, setMode] = useState<'test' | 'live'>('test');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);

  async function create() {
    setError(null);
    setSubmitting(true);

    try {
      const endpoint = await webhooks.create({
        url,
        mode,
        description: description || undefined,
      });

      setSecret(endpoint.secret);
      setCreating(false);
      setUrl('');
      setDescription('');
      await endpoints.reload();
    } catch (caught) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not add the endpoint');
    } finally {
      setSubmitting(false);
    }
  }

  async function remove(id: string) {
    if (!confirm('Delete this endpoint? Pending deliveries will be dropped.')) return;

    try {
      await webhooks.remove(id);
      await endpoints.reload();
    } catch (caught) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not delete the endpoint');
    }
  }

  async function toggle(id: string, status: string) {
    try {
      await webhooks.update(id, { status: status === 'active' ? 'paused' : 'active' });
      await endpoints.reload();
    } catch (caught) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not update the endpoint');
    }
  }

  return (
    <>
      <div className="page-header">
        <h1>Webhooks</h1>
        <p>Receive events as they happen. Every delivery is signed and retried on failure.</p>
      </div>

      {error && <Alert>{error}</Alert>}

      <Card
        title="Endpoints"
        actions={
          <button className="btn btn-primary btn-sm" onClick={() => setCreating(true)}>
            Add endpoint
          </button>
        }
      >
        {endpoints.loading ? (
          <Spinner />
        ) : !endpoints.data?.length ? (
          <Empty>No endpoints configured.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>URL</th>
                  <th>Mode</th>
                  <th>Status</th>
                  <th>Events</th>
                  <th>Failures</th>
                  <th>Last delivery</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {endpoints.data.map((endpoint) => (
                  <tr key={endpoint.id}>
                    <td className="mono">{endpoint.url}</td>
                    <td>
                      <Badge tone={endpoint.mode === 'live' ? 'accent' : 'neutral'}>
                        {endpoint.mode}
                      </Badge>
                    </td>
                    <td>
                      <Badge tone={endpoint.status === 'active' ? 'success' : 'warning'}>
                        {endpoint.status}
                      </Badge>
                    </td>
                    <td className="muted">
                      {endpoint.events.length === 0 ? 'all' : endpoint.events.length}
                    </td>
                    <td>
                      {endpoint.consecutive_failures > 0 ? (
                        <Badge tone="danger">{endpoint.consecutive_failures}</Badge>
                      ) : (
                        <span className="muted">0</span>
                      )}
                    </td>
                    <td className="muted">{formatRelative(endpoint.last_delivery_at)}</td>
                    <td style={{ display: 'flex', gap: 6 }}>
                      <button
                        className="btn btn-sm"
                        onClick={() => toggle(endpoint.id, endpoint.status)}
                      >
                        {endpoint.status === 'active' ? 'Pause' : 'Resume'}
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => remove(endpoint.id)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {creating && (
        <Modal
          title="Add webhook endpoint"
          onClose={() => setCreating(false)}
          footer={
            <>
              <button className="btn" onClick={() => setCreating(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={create} disabled={submitting || !url}>
                {submitting ? 'Adding…' : 'Add endpoint'}
              </button>
            </>
          }
        >
          <label className="field">
            <span>URL</span>
            <input
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              placeholder="https://example.com/webhooks/difference"
              autoFocus
            />
          </label>

          <label className="field">
            <span>Mode</span>
            <select value={mode} onChange={(event) => setMode(event.target.value as 'test' | 'live')}>
              <option value="test">Test</option>
              <option value="live">Live</option>
            </select>
          </label>

          <label className="field">
            <span>Description</span>
            <input
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Optional"
            />
          </label>
        </Modal>
      )}

      {secret && (
        <Modal
          title="Signing secret"
          onClose={() => setSecret(null)}
          footer={
            <button className="btn btn-primary" onClick={() => setSecret(null)}>
              Done
            </button>
          }
        >
          <Alert tone="warning">
            Store this secret — it is shown once and is required to verify delivery signatures.
          </Alert>

          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <div className="secret-reveal" style={{ flex: 1 }}>
              {secret}
            </div>
            <CopyButton value={secret} />
          </div>
        </Modal>
      )}
    </>
  );
}
